/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { DataGrid } from '@mui/x-data-grid';
import { withTheme } from '@emotion/react';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { TABLE_HEADER_FONT_COLOR } from 'modules/common/constants/style';
import PAGINATION from 'modules/common/constants/pagination-limits';
import { selectLlmLogs, selectLogsLoader } from '../../selectors';
import { getInputTokens, getTotalTokens } from '../../utils';

const columnVisibilityModel = {
  id: false,
};
//
const columns = [
  {
    field: 'id',
    headerName: 'ID',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  {
    field: 'createdAt',
    headerName: 'Time',
    headerClassName: 'super-app-theme--header',
    flex: 1,
    valueFormatter: ({ value }) => (value ? format(new Date(value), 'yyyy-MM-dd HH:mm:ss') : ''),
  },
  {
    field: 'requestId',
    headerName: 'Request ID',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  {
    field: 'provider',
    headerName: 'Provider',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  {
    field: 'model',
    headerName: 'Model',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  {
    field: 'modelVersion',
    headerName: 'Model Version',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  {
    field: 'operation',
    headerName: 'Operation',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  {
    field: 'type',
    headerName: 'Type',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  {
    field: 'finishReason',
    headerName: 'Finish Reason',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  {
    field: 'inputTokenCount',
    headerName: 'Input tokens',
    headerClassName: 'super-app-theme--header',
    flex: 1,
    valueGetter: ({ row }) => getInputTokens(row.tokenUsage),
  },
  {
    field: 'outputTokenCount',
    headerName: 'Output tokens',
    headerClassName: 'super-app-theme--header',
    flex: 1,
    valueGetter: ({ row }) => row.tokenUsage?.outputTokenCount ?? 0,
  },
  {
    field: 'cachedTokenCount',
    headerName: 'Cached tokens',
    headerClassName: 'super-app-theme--header',
    flex: 1,
    valueGetter: ({ row }) => row.tokenUsage?.cachedTokenCount ?? 0,
  },
  {
    field: 'totalTokenCount',
    headerName: 'Total tokens',
    headerClassName: 'super-app-theme--header',
    flex: 1,
    valueGetter: ({ row }) => getTotalTokens(row.tokenUsage),
  },
  {
    field: 'totalCost',
    headerName: 'Cost (USD)',
    headerClassName: 'super-app-theme--header',
    flex: 1,
    valueGetter: ({ row }) => row.cost?.totalCost,
    valueFormatter: ({ value }) =>
      value === null || value === undefined ? 'N/A' : `$${Number(value).toFixed(4)}`,
  },
  {
    field: 'durationMs',
    headerName: 'Duration (ms)',
    headerClassName: 'super-app-theme--header',
    flex: 1,
  },
  {
    field: 'hasImage',
    headerName: 'Has Image',
    headerClassName: 'super-app-theme--header',
    flex: 1,
    valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
  },
];
//
const LlmActivityTableView = ({ onPageChange }) => {
  const llmLogs = useSelector(selectLlmLogs);
  const logsLoading = useSelector(selectLogsLoader);
  //
  return (
    <div style={{ height: 500, flex: 1 }}>
      <DataGrid
        sx={{
          '& .super-app-theme--header': {
            backgroundColor: 'white',
            color: TABLE_HEADER_FONT_COLOR,
          },
          '& .MuiDataGrid-main': {
            backgroundColor: 'white',
          },
          '.MuiDataGrid-footerContainer': {
            backgroundColor: 'white',
          },
        }}
        columnVisibilityModel={columnVisibilityModel}
        rowHeight={50}
        rows={llmLogs?.results ?? []}
        columns={columns}
        paginationMode="server"
        rowCount={llmLogs?.totalResults ?? 0}
        pageSize={llmLogs?.limit ?? PAGINATION.LLM_LOGS_PER_PAGE_COUNT}
        page={(llmLogs?.page ?? 1) - 1}
        rowsPerPageOptions={[PAGINATION.LLM_LOGS_PER_PAGE_COUNT]}
        onPageChange={onPageChange}
        loading={logsLoading}
        disableSelectionOnClick
      />
    </div>
  );
};
//
export default withTheme(LlmActivityTableView);
